import React, { useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import QuestionPanel from './QuestionPanel';
import ExplanationPanel from './ExplanationPanel';

const QuizContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const PanelContainer = styled.div<{ width: string }>`
  flex: ${({ width }) => width};
  overflow: hidden;
  display: flex;
  min-width: 0; /* Allow flexbox to shrink */
`;

const ResizeHandle = styled.div`
  width: 6px;
  background-color: ${({ theme }) => theme.colors.border.medium};
  cursor: col-resize;
  transition: background-color ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
  }
  
  &:active {
    background-color: ${({ theme }) => theme.colors.tertiary};
  }
`;

const Quiz: React.FC = () => {
  const [leftPanelWidth, setLeftPanelWidth] = useState('1');
  const [rightPanelWidth, setRightPanelWidth] = useState('1');
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const leftWidthRef = useRef(0);
  const rightWidthRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleResize = useCallback((e: MouseEvent) => {
    if (!isResizing || !containerRef.current) return;
    
    const containerWidth = containerRef.current.offsetWidth;
    const deltaX = e.clientX - startXRef.current;
    
    // Calculate new widths ensuring minimum sizes
    const minWidth = containerWidth * 0.2; // Minimum 20% width for any panel
    
    let newLeftWidth = leftWidthRef.current + deltaX;
    let newRightWidth = rightWidthRef.current - deltaX;
    
    // Enforce minimum widths
    if (newLeftWidth < minWidth) {
      newLeftWidth = minWidth;
      newRightWidth = containerWidth - minWidth;
    } else if (newRightWidth < minWidth) {
      newRightWidth = minWidth;
      newLeftWidth = containerWidth - minWidth;
    }
    
    // Set new flex ratios
    setLeftPanelWidth(String(newLeftWidth));
    setRightPanelWidth(String(newRightWidth));
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    window.removeEventListener('mousemove', handleResize);
    window.removeEventListener('mouseup', handleResizeEnd);
  }, [handleResize]);
  
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    
    // Store the starting position and initial widths
    startXRef.current = e.clientX;
    
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      leftWidthRef.current = (containerWidth / 2) * (Number(leftPanelWidth) / (Number(leftPanelWidth) + Number(rightPanelWidth)));
      rightWidthRef.current = (containerWidth / 2) * (Number(rightPanelWidth) / (Number(leftPanelWidth) + Number(rightPanelWidth)));
    }
    
    // Add window event listeners
    window.addEventListener('mousemove', handleResize);
    window.addEventListener('mouseup', handleResizeEnd);
  }, [leftPanelWidth, rightPanelWidth, handleResize, handleResizeEnd]);
  
  return (
    <QuizContainer ref={containerRef}>
      <PanelContainer width={leftPanelWidth}>
        <QuestionPanel />
      </PanelContainer>
      
      <ResizeHandle onMouseDown={handleResizeStart} />
      
      <PanelContainer width={rightPanelWidth}>
        <ExplanationPanel />
      </PanelContainer>
    </QuizContainer>
  );
};

export default Quiz; 